import React, { useEffect, useState } from 'react';
import FieldTypeSelector from '../investments/addInvestment/inputs/Index';
import { CompanyServiceState } from '../../redux/companyServices/companyServicesSlice';
import {
  commonFieldConfig,
  commonFieldTypes,
} from '../investments/addInvestment/inputs/field.config';
import { AppDispatch, RootState } from '../../redux/store';
import { useDispatch, useSelector } from 'react-redux';
import {
  clearEditCompanyServiceData,
  setEditCompanyServiceData,
  updateEditServiceFieldValue,
} from '../../redux/companyServices/editCompanyServicesSlice';
import { useNavigate, useParams } from 'react-router-dom';
import GetByIdApi from '../../api/genericApis/GetById.api';
import UpdateApi from '../../api/genericApis/Update.api';
import Swal from 'sweetalert2';
import Skeleton from 'react-loading-skeleton'; // Import Skeleton
import { ModuleType } from '../../api/modules.types';

export default function EditService() {
  const dispatch: AppDispatch = useDispatch();
  const serviceData = useSelector(
    (state: RootState) => state.editCompanyServices,
  );
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [loading, setLoading] = useState<boolean>(true); // Loading state

  const fetchData = async () => {
    if (!id) return;
    try {
      const data = (
        await GetByIdApi({ moduleType: ModuleType.service, id: +id })
      ).data as unknown as CompanyServiceState;
      dispatch(setEditCompanyServiceData({ serviceData: data }));
      setLoading(false); // Data loaded, set loading to false
    } catch (error) {
      console.error('Error fetching data:', error);
      navigate('/services');
    }
  };

  useEffect(() => {
    fetchData();
    return () => {
      dispatch(clearEditCompanyServiceData()); // Clean up on unmount
    };
  }, [id]);

  // Handle input changes for fields
  const handleInputChange = ({
    e,
    field,
    lang,
    isSameValue,
  }: {
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>;
    field: string;
    lang: 'ar' | 'en';
    isSameValue: boolean;
  }) => {
    const { value } = e.target;

    dispatch(
      updateEditServiceFieldValue({
        language: lang,
        field,
        value,
        isSameValue,
      }),
    );
  };

  const headerSectionFieldsEn = [
    {
      label: 'Service Name',
      value: serviceData.en.serviceName,
      field: 'serviceName',
      config: { ...commonFieldConfig['en'][commonFieldTypes.text] },
    },
    {
      label: 'Service Description',
      value: serviceData.en.serviceDescription,
      field: 'serviceDescription',
      config: { ...commonFieldConfig['en'][commonFieldTypes.text] },
    },
  ];

  const headerSectionFieldsAr = [
    {
      label: 'اسم الخدمة',
      value: serviceData.ar.serviceName,
      field: 'serviceName',
      config: { ...commonFieldConfig['ar'][commonFieldTypes.text] },
    },
    {
      label: 'وصف الخدمة',
      value: serviceData.ar.serviceDescription,
      field: 'serviceDescription',
      config: { ...commonFieldConfig['ar'][commonFieldTypes.text] },
    },
  ];

  const imageSection = {
    label: 'Service Image URL',
    value: serviceData.en.serviceImage,
    field: 'serviceImage',
    config: { ...commonFieldConfig['en'][commonFieldTypes.image] },
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Confirm with SweetAlert before submitting
    Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to submit the updated service data?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, submit!',
      cancelButtonText: 'No, cancel',
    }).then((result) => {
      if (result.isConfirmed) {
        UpdateApi({
          moduleType: ModuleType.service,
          id: Number(id),
          data: serviceData,
        })
          .then(() => {
            // On success
            Swal.fire({
              title: 'Success!',
              text: 'Service has been successfully updated.',
              icon: 'success',
              confirmButtonText: 'Ok',
            });

            // Navigate to the service table page after success
            navigate('/services');
          })
          .catch((error) => {
            // On error
            Swal.fire({
              title: 'Error!',
              text: 'There was an issue updating the service. Please try again.',
              icon: 'error',
              confirmButtonText: 'Ok',
            });
          });
      }
    });
  };

  return (
    <section className="mb-5 d-flex flex-column">
      <h3 className="mb-3">Edit Service</h3>

      <div className="row">
        <div className="col-md-6">
          {loading ? (
            <>
              <Skeleton height={24} width="25%" className="mb-3" />
              <Skeleton height={38} width="100%" className="mb-3" />
              <Skeleton height={24} width="25%" className="mb-3" />
              <Skeleton height={38} width="100%" className="mb-3" />
            </>
          ) : (
            headerSectionFieldsEn.map((field) => (
              <FieldTypeSelector
                fieldName={field.field}
                key={field.field}
                label={field.label}
                value={field.value}
                config={field.config}
                onInputChange={(e) =>
                  handleInputChange({
                    e,
                    field: field.field,
                    lang: 'en',
                    isSameValue: field.config.isSameValue,
                  })
                }
              />
            ))
          )}
        </div>

        <div className="col-md-6" dir="rtl">
          {loading ? (
            <>
              <Skeleton height={24} width="25%" className="mb-3" />
              <Skeleton height={38} width="100%" className="mb-3" />
              <Skeleton height={24} width="25%" className="mb-3" />
              <Skeleton height={38} width="100%" className="mb-3" />
            </>
          ) : (
            headerSectionFieldsAr.map((field) => (
              <FieldTypeSelector
                fieldName={field.field}
                label={field.label}
                value={field.value}
                config={field.config}
                onInputChange={(e) =>
                  handleInputChange({
                    e,
                    field: field.field,
                    lang: 'ar',
                    isSameValue: field.config.isSameValue,
                  })
                }
              />
            ))
          )}
        </div>
      </div>

      {loading ? (
        <div className="d-flex flex-wrap flex-md-nowrap justify-content-between align-items-center w-100">
          <div className="w-50">
            <Skeleton height={45} width={300} className="mb-3" />
          </div>

          <div className="d-flex justify-content-center w-100">
            <Skeleton height={150} width={200} className="mb-3" />
          </div>
        </div>
      ) : (
        imageSection && (
          <FieldTypeSelector
            fieldName={imageSection.field}
            label={imageSection.label}
            value={imageSection.value}
            config={imageSection.config}
            onInputChange={(e) =>
              handleInputChange({
                e,
                field: imageSection.field,
                lang: 'en',
                isSameValue: imageSection.config?.isSameValue,
              })
            }
          />
        )
      )}

      <button
        onClick={handleSubmit}
        className="btn btn-primary mt-4 align-self-center p-2"
        disabled={loading} // Disable submit button during loading
      >
        Submit
      </button>
    </section>
  );
}
